import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  startAdzunaFetch,
  startAdzunaFinanceFetch,
  AdzunaFetchResult,
  startJobScrapeRun,
  getJobScrapeStatus,
  getJobScrapeStatusStreamUrl,
  getLastScrapeRun,
  getCurrentScrapeJob,
  getStoredJobScrapeJobId,
  setStoredJobScrapeJobId,
  JobScrapeStatusResponse,
  LastScrapeRunResponse,
  ScraperPlatformProgress,
  fetchGoogleAiJobs,
  GoogleAiJobsResult,
} from '../services/connectionsService';
import { getResumes } from '../services/resumeService';
import type { DetailedResume } from '../types/resume';

const ADZUNA_LIMIT_KEY = 'adzuna-fetch-limit';
const ADZUNA_FINANCE_LIMIT_KEY = 'adzuna-finance-fetch-limit';
const ADZUNA_FINANCE_MAX_EMP_KEY = 'adzuna-finance-max-employers';

/**
 * Optional Adzuna fetch — adds jobs to the shared database for everyone.
 * Title filtering and saving matches for your resume happen on Recommended jobs (Step 1).
 */
const JobSearchDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [error, setError] = useState('');
  const [adzunaLimit, setAdzunaLimit] = useState<number>(() => {
    const stored = localStorage.getItem(ADZUNA_LIMIT_KEY);
    return stored !== null ? Math.max(1, Math.min(100, Number(stored) || 20)) : 20;
  });
  const [adzunaFetching, setAdzunaFetching] = useState(false);
  const [adzunaResult, setAdzunaResult] = useState<AdzunaFetchResult | null>(null);
  const [financeLimit, setFinanceLimit] = useState<number>(() => {
    const stored = localStorage.getItem(ADZUNA_FINANCE_LIMIT_KEY);
    return stored !== null ? Math.max(1, Math.min(100, Number(stored) || 30)) : 30;
  });
  const [financeFetching, setFinanceFetching] = useState(false);
  const [financeResult, setFinanceResult] = useState<AdzunaFetchResult | null>(null);
  /** Empty string = query all configured employers (see server JSON). */
  const [financeMaxEmployers, setFinanceMaxEmployers] = useState<string>(() => {
    const stored = localStorage.getItem(ADZUNA_FINANCE_MAX_EMP_KEY);
    return stored !== null && stored !== '' ? stored : '';
  });
  const [showAdzunaPanel, setShowAdzunaPanel] = useState(true);
  const [showJobScraperPanel, setShowJobScraperPanel] = useState(true);
  const [scrapeNotifyEmail, setScrapeNotifyEmail] = useState('');
  const [scrapeRunning, setScrapeRunning] = useState(false);
  const [scrapeStatus, setScrapeStatus] = useState<JobScrapeStatusResponse | null>(null);
  const [lastScrapeRun, setLastScrapeRun] = useState<LastScrapeRunResponse | null>(null);
  const evtSourceRef = useRef<EventSource | null>(null);
  const sseRetriesRef = useRef(0);
  const sseNotFoundRef = useRef(0);
  /** Tracks which job ID is currently "active" so stale SSE events from an old
   *  stream can't clobber state after the user starts a new run or navigates away. */
  const activeJobIdRef = useRef<string | null>(null);
  const [showGeminiPanel, setShowGeminiPanel] = useState(true);
  const [geminiLoading, setGeminiLoading] = useState(false);
  const [geminiResult, setGeminiResult] = useState<GoogleAiJobsResult | null>(null);
  const [geminiGrounding, setGeminiGrounding] = useState(true);
  const [resumes, setResumes] = useState<DetailedResume[]>([]);
  const [selectedResumeId, setSelectedResumeId] = useState('');

  const stopScrapeStream = () => {
    if (evtSourceRef.current) {
      evtSourceRef.current.close();
      evtSourceRef.current = null;
    }
    activeJobIdRef.current = null;
    sseRetriesRef.current = 0;
  };

  /**
   * Open an SSE stream for the given scraper job.
   * On error (including Firebase token expiry after 1 hour), reconnects with a
   * fresh token. Retries reset on each successful message so a brief disconnect
   * after a long healthy run doesn't exhaust the counter. Max 10 consecutive
   * failures (without a successful message between them) before giving up.
   */
  const connectScrapeSSE = async (jobId: string) => {
    if (!user) return;
    const token = await user.getIdToken();
    const url = getJobScrapeStatusStreamUrl(jobId, token);
    const es = new EventSource(url);
    evtSourceRef.current = es;

    es.onmessage = (e: MessageEvent) => {
      if (jobId !== activeJobIdRef.current) { es.close(); return; }
      // Reset retry counter — connection is healthy.
      sseRetriesRef.current = 0;
      try {
        const st = JSON.parse(e.data) as JobScrapeStatusResponse & { status: string };
        // Backend signals cross-instance miss (e.g. GCR routed us to a new instance).
        // Count consecutive not_found responses; give up after 5 so a GCR restart
        // doesn't leave the button stuck in "Scraper running…" forever.
        if ((st as any).status === 'not_found') {
          sseNotFoundRef.current += 1;
          if (sseNotFoundRef.current >= 5) {
            // Stop the stale stream before checking for a live job.
            stopScrapeStream();
            void (async () => {
              try {
                if (!user) throw new Error('no user');
                const tkn = await user.getIdToken();
                const cur = await getCurrentScrapeJob(tkn);
                if (cur.jobId && cur.jobId !== jobId) {
                  // A different instance is running this user's job — reconnect to it.
                  sseNotFoundRef.current = 0;
                  setStoredJobScrapeJobId(cur.jobId);
                  setScrapeRunning(true);
                  activeJobIdRef.current = cur.jobId;
                  await connectScrapeSSE(cur.jobId);
                  return;
                }
              } catch {
                // No active job found — fall through to give-up state.
              }
              setScrapeRunning(false);
              setStoredJobScrapeJobId(null);
              setError('Scraper job not found — the server may have restarted. Please run again.');
            })();
          }
          return;
        }
        sseNotFoundRef.current = 0;
        setScrapeStatus(st);
        if (st.status === 'completed' || st.status === 'failed' || st.status === 'idle') {
          stopScrapeStream();
          setScrapeRunning(false);
          setStoredJobScrapeJobId(null);
        }
      } catch { /* ignore parse errors */ }
    };

    es.onerror = async () => {
      if (jobId !== activeJobIdRef.current) { es.close(); return; }
      es.close();
      evtSourceRef.current = null;
      sseRetriesRef.current += 1;
      if (sseRetriesRef.current > 10) {
        // 10 consecutive failures without a successful message — give up.
        setScrapeRunning(false);
        setError('Lost connection to scraper status. The scraper may still be running — refresh to check.');
        return;
      }
      // Back-off capped at 10 s, then reconnect with a fresh token (handles the
      // 1-hour Firebase token expiry for long Workable runs).
      const delay = Math.min(1500 * sseRetriesRef.current, 10000);
      await new Promise(r => setTimeout(r, delay));
      if (jobId !== activeJobIdRef.current) return;
      try {
        await connectScrapeSSE(jobId);
      } catch (err: any) {
        setScrapeRunning(false);
        setError(err.message || 'Failed to reconnect to scraper status stream.');
      }
    };
  };

  useEffect(() => {
    const stored = getStoredJobScrapeJobId();
    if (!user || !stored) return;
    activeJobIdRef.current = stored;
    setScrapeRunning(true);
    void connectScrapeSSE(stored);
    return () => stopScrapeStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await user.getIdToken();
        const run = await getLastScrapeRun(token);
        if (!cancelled) setLastScrapeRun(run);
      } catch {
        // No last run yet — that's fine
      }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await getResumes();
        if (!cancelled && Array.isArray(list)) {
          setResumes(list);
        }
      } catch {
        if (!cancelled) setResumes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  const handleGeminiFetch = async () => {
    if (!user) return;
    setGeminiLoading(true);
    setError('');
    setGeminiResult(null);
    try {
      const token = await user.getIdToken();
      const result = await fetchGoogleAiJobs(token, selectedResumeId || null, geminiGrounding);
      setGeminiResult(result);
    } catch (err: any) {
      setError(err.message || 'Gemini job search failed.');
    } finally {
      setGeminiLoading(false);
    }
  };

  const handleJobScrapeStart = async () => {
    if (!user) return;
    setScrapeRunning(true);
    setError('');
    setScrapeStatus(null);
    sseNotFoundRef.current = 0;
    // stopScrapeStream() also clears activeJobIdRef so stale SSE events from
    // a previous run can't clobber the new run's state.
    stopScrapeStream();
    try {
      const token = await user.getIdToken();
      const start = await startJobScrapeRun(token, scrapeNotifyEmail || user.email || null);
      // Set the active job ID BEFORE opening the stream so any concurrent event
      // arriving during the async gap is correctly attributed.
      activeJobIdRef.current = start.jobId;
      setStoredJobScrapeJobId(start.jobId);
      await connectScrapeSSE(start.jobId);
    } catch (err: any) {
      setError(err.message || 'Job scrape failed to start.');
      setScrapeRunning(false);
    }
  };

  const handleAdzunaFetch = async () => {
    if (!user) return;
    setAdzunaFetching(true);
    setError('');
    setAdzunaResult(null);
    localStorage.setItem(ADZUNA_LIMIT_KEY, String(adzunaLimit));
    try {
      const token = await user.getIdToken();
      const result = await startAdzunaFetch(token, adzunaLimit);
      setAdzunaResult(result);
    } catch (err: any) {
      setError(err.message || 'Adzuna fetch failed.');
    } finally {
      setAdzunaFetching(false);
    }
  };

  const handleAdzunaFinanceFetch = async (force: boolean = false) => {
    if (!user) return;
    setFinanceFetching(true);
    setError('');
    setFinanceResult(null);
    localStorage.setItem(ADZUNA_FINANCE_LIMIT_KEY, String(financeLimit));
    localStorage.setItem(ADZUNA_FINANCE_MAX_EMP_KEY, financeMaxEmployers.trim());
    const maxEmpRaw = financeMaxEmployers.trim();
    let maxEmployers: number | undefined;
    if (maxEmpRaw !== '') {
      const n = parseInt(maxEmpRaw, 10);
      if (!Number.isNaN(n) && n >= 1) {
        maxEmployers = Math.min(50, n);
      }
    }
    try {
      const token = await user.getIdToken();
      const result = await startAdzunaFinanceFetch(token, financeLimit, force, maxEmployers);
      setFinanceResult(result);
    } catch (err: any) {
      setError(err.message || 'Finance Adzuna fetch failed.');
    } finally {
      setFinanceFetching(false);
    }
  };

  const handleAdzunaUsSeniorFinanceFetch = async () => {
    if (!user) return;
    setFinanceFetching(true);
    setError('');
    setFinanceResult(null);
    localStorage.setItem(ADZUNA_FINANCE_LIMIT_KEY, String(financeLimit));
    localStorage.setItem(ADZUNA_FINANCE_MAX_EMP_KEY, financeMaxEmployers.trim());
    const maxEmpRaw = financeMaxEmployers.trim();
    let maxEmployers: number | undefined;
    if (maxEmpRaw !== '') {
      const n = parseInt(maxEmpRaw, 10);
      if (!Number.isNaN(n) && n >= 1) {
        maxEmployers = Math.min(50, n);
      }
    }
    try {
      const token = await user.getIdToken();
      const result = await startAdzunaFinanceFetch(
        token,
        financeLimit,
        false,
        maxEmployers,
        true,
        true,
      );
      setFinanceResult(result);
    } catch (err: any) {
      setError(err.message || 'Finance Adzuna fetch failed.');
    } finally {
      setFinanceFetching(false);
    }
  };

  return (
    <div className="min-h-screen bg-ts-cream">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <div className="space-y-4">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-3">Fetch Additional Jobs</h1>
            <p className="text-gray-600 max-w-xl mx-auto text-sm">
              Pull extra postings into the shared job database (Adzuna, company career sites: Greenhouse, Amazon,
              Workday, etc., and Google Careers via Gemini). To scan the database and save matches for{' '}
              <strong>your</strong> resume,
              use{' '}
              <button
                type="button"
                onClick={() => navigate('/jobs-for-you')}
                className="text-ts-gold hover:text-ts-gold-deep underline font-medium"
              >
                Recommended jobs
              </button>{' '}
              (Step 1).
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-teal-200/80">
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-lg font-semibold text-gray-900">Adzuna — Finance jobs</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-900">
                  Accounting &amp; Finance category
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Pull <strong>finance-sector</strong> listings from Adzuna (US + CA) into the shared job database. Each run
                queries the Adzuna <span className="font-mono text-xs">accounting-finance-jobs</span> category for a{' '}
                <strong>prioritized list of employers</strong> (banks and asset managers first; systematic / market-making
                names last — configured on the server). By default, skips if finance jobs were already refreshed today
                (calendar day, UTC).
              </p>
              <p className="text-xs text-teal-800 mt-2">
                Use <strong>USA FINANCE SENIOR</strong> for a focused test mode (US-only locations + senior titles only).
              </p>
              <div className="mt-4 flex flex-col sm:flex-row sm:items-end gap-3 flex-wrap">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max jobs to write</label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={financeLimit}
                    onChange={(e) =>
                      setFinanceLimit(Math.max(1, Math.min(100, Number(e.target.value) || 1)))
                    }
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max employers (optional)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    placeholder="All"
                    value={financeMaxEmployers}
                    onChange={(e) => setFinanceMaxEmployers(e.target.value.replace(/[^\d]/g, ''))}
                    className="w-28 px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 placeholder:text-gray-400"
                    title="Only query the first N employers in server order (e.g. 5 for a small test). Leave empty for the full list."
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => void handleAdzunaUsSeniorFinanceFetch()}
                    disabled={financeFetching}
                    className="px-4 py-2 text-sm font-semibold bg-slate-800 text-white rounded-lg hover:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="US-only finance fetch with senior-level title filtering; title diversity prioritized"
                  >
                    USA FINANCE SENIOR
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleAdzunaFinanceFetch(false)}
                    disabled={financeFetching}
                    className="px-5 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {financeFetching ? 'Fetching finance jobs…' : 'Fetch finance jobs (Adzuna)'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleAdzunaFinanceFetch(true)}
                    disabled={financeFetching}
                    className="px-4 py-2 text-sm font-medium border border-teal-300 text-teal-800 rounded-lg hover:bg-teal-50 disabled:opacity-50"
                    title="Bypass the once-per-day skip and call Adzuna again"
                  >
                    Force refresh
                  </button>
                </div>
              </div>
              {financeResult && (
                <div className="mt-4 bg-teal-50 border border-teal-200 rounded-lg px-4 py-3 space-y-1">
                  <p className="text-sm text-teal-900 font-medium">{financeResult.message}</p>
                  <p className="text-xs text-teal-800">
                    API calls: {financeResult.apiCalls}
                    {financeResult.companiesFetched.length > 0 && (
                      <>
                        {' '}
                        · Employers queried:{' '}
                        {financeResult.companiesFetched.length > 12
                          ? `${financeResult.companiesFetched.slice(0, 12).join(', ')}… (+${financeResult.companiesFetched.length - 12} more)`
                          : financeResult.companiesFetched.join(', ')}
                      </>
                    )}
                  </p>
                  {financeResult.titleDiversityEnabled && (
                    <p className="text-xs text-teal-800">
                      Title diversity prioritization: on
                      {typeof financeResult.uniqueTitlesWritten === 'number' && (
                        <>
                          {' '}
                          · Unique titles written: {financeResult.uniqueTitlesWritten}
                        </>
                      )}
                      {typeof financeResult.uniqueTitlesInApi === 'number' && (
                        <>
                          {' '}
                          · Unique titles in API results: {financeResult.uniqueTitlesInApi}
                        </>
                      )}
                    </p>
                  )}
                  {financeResult.companiesCached.length > 0 && (
                    <p className="text-xs text-teal-800">
                      {financeResult.companiesCached.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-ts-border">
            <button
              type="button"
              onClick={() => setShowAdzunaPanel((prev) => !prev)}
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-lg font-semibold text-gray-900">Adzuna</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  Shared database
                </span>
              </div>
              <span className="text-gray-400 text-lg">{showAdzunaPanel ? '▲' : '▼'}</span>
            </button>

            {showAdzunaPanel && (
              <div className="px-6 pb-6 border-t border-gray-100 pt-4 space-y-4">
                <p className="text-sm text-gray-600">
                  Populate the shared job database from Adzuna for your top connected companies (excluding
                  Greenhouse-covered and Google). Jobs are saved with descriptions and benefit{' '}
                  <strong>all users</strong> when they run Step 1 on Recommended jobs.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max jobs to fetch</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={adzunaLimit}
                      onChange={(e) =>
                        setAdzunaLimit(Math.max(1, Math.min(100, Number(e.target.value) || 1)))
                      }
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    {[10, 20, 40].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setAdzunaLimit(n)}
                        className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                          adzunaLimit === n
                            ? 'bg-orange-100 border-orange-300 text-orange-800'
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Free-tier rate limit: 250 calls/day, 25/min. Jobs are spread across up to 50 companies by
                    connectivity score.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleAdzunaFetch}
                  disabled={adzunaFetching}
                  className="px-5 py-2 text-sm font-medium bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {adzunaFetching ? 'Fetching from Adzuna…' : 'Fetch Adzuna Jobs'}
                </button>

                {adzunaResult && (
                  <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                    <p className="text-sm text-green-800 font-medium">{adzunaResult.message}</p>
                    {adzunaResult.titleDiversityEnabled && (
                      <p className="text-xs text-green-700 mt-1">
                        Title diversity prioritization is active for this run.
                      </p>
                    )}
                    {adzunaResult.companiesFetched.length > 0 && (
                      <p className="text-xs text-green-700 mt-1">
                        New: {adzunaResult.companiesFetched.join(', ')}
                      </p>
                    )}
                    {adzunaResult.companiesCached.length > 0 && (
                      <p className="text-xs text-green-700 mt-1">
                        Cached (today): {adzunaResult.companiesCached.join(', ')}
                      </p>
                    )}
                    <p className="text-xs text-green-600 mt-2">
                      Next: go to{' '}
                      <button
                        type="button"
                        onClick={() => navigate('/jobs-for-you')}
                        className="font-semibold underline hover:text-green-800"
                      >
                        Recommended jobs
                      </button>{' '}
                      and run Step 1 for your resume to pick up new postings.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-ts-border">
            <button
              type="button"
              onClick={() => setShowJobScraperPanel((prev) => !prev)}
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-lg font-semibold text-gray-900">Company job boards</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  Greenhouse · Amazon · …
                </span>
              </div>
              <span className="text-gray-400 text-lg">{showJobScraperPanel ? '▲' : '▼'}</span>
            </button>

            {showJobScraperPanel && (
              <div className="px-6 pb-6 border-t border-gray-100 pt-4 space-y-4">
                <p className="text-sm text-gray-600">
                  Scrapes Greenhouse, Lever, Ashby, Workable, SmartRecruiters, Recruitee, Pinpoint, Personio, Comeet, and Amazon via their
                  public APIs — up to <strong className="text-gray-900">500</strong> postings per
                  company, no total cap. Only jobs newer than the latest stored posting for each company are fetched.
                  Results go into the shared{' '}
                  <code className="text-xs bg-gray-100 text-gray-800 px-1 rounded border border-gray-200">
                    job-post-urls
                  </code>{' '}
                  collection. You will receive an email when the run finishes (optional address below; defaults to your
                  account email).
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notify email (optional)
                  </label>
                  <input
                    type="email"
                    value={scrapeNotifyEmail}
                    onChange={(e) => setScrapeNotifyEmail(e.target.value)}
                    placeholder={user?.email || 'your@email.com'}
                    className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleJobScrapeStart}
                  disabled={scrapeRunning}
                  className="px-5 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {scrapeRunning ? 'Scraper running…' : 'Run job board scraper'}
                </button>

                {/* Last run summary — shown when no active run is in progress */}
                {!scrapeStatus && lastScrapeRun && (
                  <div className={`rounded-lg px-4 py-3 text-sm ${
                    lastScrapeRun.status === 'failed'
                      ? 'bg-red-50 border border-red-200 text-red-800'
                      : lastScrapeRun.status === 'running'
                      ? 'bg-amber-50 border border-amber-200 text-amber-900'
                      : 'bg-gray-50 border border-gray-200 text-gray-700'
                  }`}>
                    <p className="font-medium mb-2">
                      {(() => {
                        const ts = lastScrapeRun.completedAt ?? lastScrapeRun.startedAt;
                        return `Last run${ts ? ` · ${new Date(ts).toLocaleString()}` : ''}`;
                      })()}
                      {typeof lastScrapeRun.durationSec === 'number' && (
                        <span className="font-normal opacity-70"> · {lastScrapeRun.durationSec.toFixed(0)}s</span>
                      )}
                      {lastScrapeRun.status === 'failed' && (
                        <span className="ml-2 font-normal text-red-600">— failed</span>
                      )}
                      {lastScrapeRun.status === 'running' && (
                        <span className="ml-2 font-normal text-amber-700">— interrupted</span>
                      )}
                    </p>
                    {lastScrapeRun.error && (
                      <p className="mb-2 text-xs text-red-700">{lastScrapeRun.error}</p>
                    )}
                    {lastScrapeRun.progress && Object.keys(lastScrapeRun.progress).length > 0 && (
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="opacity-60">
                            <th className="text-left font-medium pb-1 pr-3">Platform</th>
                            <th className="text-right font-medium pb-1 pr-3">Companies</th>
                            <th className="text-right font-medium pb-1">Jobs imported</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(['Greenhouse', 'Lever', 'Ashby', 'Workable', 'Smartrecruiters', 'Recruitee', 'Pinpoint', 'Personio', 'Comeet', 'Workday', 'Amazon'] as const)
                            .filter((p) => lastScrapeRun.progress[p] !== undefined)
                            .map((platform) => {
                              const p = lastScrapeRun.progress[platform] as ScraperPlatformProgress;
                              return (
                                <tr key={platform} className="border-t border-current/10">
                                  <td className="py-1 pr-3 font-medium whitespace-nowrap">{platform}</td>
                                  <td className="py-1 pr-3 text-right tabular-nums">{p.processed}/{p.total}</td>
                                  <td className="py-1 text-right tabular-nums font-semibold">
                                    {p.inserted > 0 ? p.inserted : <span className="opacity-40 font-normal">—</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          <tr className="border-t border-current/20 font-medium">
                            <td className="pt-1.5 pr-3">Total</td>
                            <td className="pt-1.5 pr-3 text-right tabular-nums"></td>
                            <td className="pt-1.5 text-right tabular-nums">{lastScrapeRun.inserted}</td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                    {lastScrapeRun.errors && lastScrapeRun.errors.length > 0 && (
                      <div className="mt-3 border-t border-red-200 pt-3">
                        <p className="text-xs font-semibold text-red-700 mb-1.5">
                          ⚠ Broken sources ({lastScrapeRun.errors.length})
                        </p>
                        <ul className="space-y-1">
                          {lastScrapeRun.errors.map((e, i) => (
                            <li key={i} className="text-xs text-red-700">
                              <span className="font-medium">{e.company}</span>
                              <span className="opacity-70"> [{e.platform}]</span>
                              {' — '}
                              <span>{e.error}</span>
                              {e.url && (
                                <a
                                  href={e.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-1 underline opacity-60 hover:opacity-100"
                                >
                                  ↗
                                </a>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {scrapeStatus && (
                  <div
                    className={`rounded-lg px-4 py-3 text-sm ${
                      scrapeStatus.status === 'failed'
                        ? 'bg-red-50 border border-red-200 text-red-800'
                        : scrapeStatus.status === 'completed'
                          ? 'bg-green-50 border border-green-200 text-green-800'
                          : 'bg-amber-50 border border-amber-200 text-amber-900'
                    }`}
                  >
                    <p className="font-medium">
                      {scrapeStatus.status === 'processing' && 'Scraper in progress…'}
                      {scrapeStatus.status === 'completed' && 'Scraper completed'}
                      {scrapeStatus.status === 'failed' && 'Scraper failed'}
                    </p>

                    {/* Per-platform progress — visible while running and after completion */}
                    {scrapeStatus.progress && Object.keys(scrapeStatus.progress).length > 0 && (
                      <div className="mt-3">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="opacity-60">
                              <th className="text-left font-medium pb-1 pr-3">Platform</th>
                              <th className="text-right font-medium pb-1 pr-3">Companies</th>
                              <th className="text-left font-medium pb-1 pr-2">Progress</th>
                              <th className="text-right font-medium pb-1">New jobs</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(
                              ['Greenhouse', 'Lever', 'Ashby', 'Workable', 'Smartrecruiters', 'Recruitee', 'Pinpoint', 'Personio', 'Comeet', 'Workday', 'Amazon'] as const
                            )
                              .filter((p) => scrapeStatus.progress![p] !== undefined)
                              .map((platform) => {
                                const p = scrapeStatus.progress![platform] as ScraperPlatformProgress;
                                const pct = p.total > 0 ? Math.round((p.processed / p.total) * 100) : 0;
                                const done = p.processed >= p.total && p.total > 0;
                                return (
                                  <tr key={platform} className="border-t border-current/10">
                                    <td className="py-1 pr-3 font-medium whitespace-nowrap">{platform}</td>
                                    <td className="py-1 pr-3 text-right tabular-nums whitespace-nowrap">
                                      {p.processed}/{p.total}
                                    </td>
                                    <td className="py-1 pr-2 w-24">
                                      <div className="h-1.5 rounded-full bg-current/10 overflow-hidden">
                                        <div
                                          className={`h-full rounded-full transition-all duration-500 ${
                                            done
                                              ? 'bg-green-500'
                                              : 'bg-indigo-500'
                                          }`}
                                          style={{ width: `${pct}%` }}
                                        />
                                      </div>
                                    </td>
                                    <td className="py-1 text-right tabular-nums">
                                      {p.inserted > 0 ? (
                                        <span className="font-semibold">{p.inserted}</span>
                                      ) : (
                                        <span className="opacity-40">—</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {scrapeStatus.result && (
                      <ul className="mt-3 text-xs space-y-1 border-t border-current/10 pt-2">
                        <li>
                          Time:{' '}
                          {typeof scrapeStatus.result.durationSec === 'number'
                            ? scrapeStatus.result.durationSec.toFixed(1)
                            : scrapeStatus.result.durationSec}
                          s
                        </li>
                        <li>
                          New inserts: {scrapeStatus.result.inserted}
                          {typeof scrapeStatus.result.skippedExisting === 'number' && (
                            <> · already in DB (skipped): {scrapeStatus.result.skippedExisting}</>
                          )}
                          {typeof scrapeStatus.result.skippedCutoff === 'number' && (
                            <> · older than latest stored date (skipped): {scrapeStatus.result.skippedCutoff}</>
                          )}
                        </li>
                        <li>Postings seen from APIs (before duplicate/cutoff checks): {scrapeStatus.result.discoveredTotal}</li>
                      </ul>
                    )}
                    {scrapeStatus.error && (
                      <p className="mt-2 text-xs text-red-700">{scrapeStatus.error}</p>
                    )}
                    {scrapeStatus.emailNotification && (
                      <p className="mt-2 text-xs opacity-90">Email: {scrapeStatus.emailNotification}</p>
                    )}
                    {scrapeStatus.result?.errors && scrapeStatus.result.errors.length > 0 && (
                      <div className="mt-3 border-t border-red-200 pt-3">
                        <p className="text-xs font-semibold text-red-700 mb-1.5">
                          ⚠ Broken sources ({scrapeStatus.result.errors.length})
                        </p>
                        <ul className="space-y-1">
                          {scrapeStatus.result.errors.map((e, i) => (
                            <li key={i} className="text-xs text-red-700">
                              <span className="font-medium">{e.company}</span>
                              <span className="opacity-70"> [{e.platform}]</span>
                              {' — '}
                              <span>{e.error}</span>
                              {e.url && (
                                <a
                                  href={e.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="ml-1 underline opacity-60 hover:opacity-100"
                                >
                                  ↗
                                </a>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-ts-border">
            <button
              type="button"
              onClick={() => setShowGeminiPanel((prev) => !prev)}
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-lg font-semibold text-gray-900">Google Careers (Gemini)</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                  careers.google.com only
                </span>
              </div>
              <span className="text-gray-400 text-lg">{showGeminiPanel ? '▲' : '▼'}</span>
            </button>

            {showGeminiPanel && (
              <div className="px-6 pb-6 border-t border-gray-100 pt-4 space-y-4">
                <p className="text-sm text-gray-600">
                  Uses Vertex Gemini with Google Search grounding to find open roles that match your resume on{' '}
                  <strong>Google&apos;s official careers site only</strong> (posting URLs must be under{' '}
                  <code className="text-xs bg-gray-100 text-gray-800 px-1 rounded border border-gray-200">
                    google.com/about/careers/applications/jobs
                  </code>
                  , anchored to the{' '}
                  <a
                    href="https://www.google.com/about/careers/applications/jobs/results"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-700 underline font-medium"
                  >
                    Google Careers job search
                  </a>
                  ). Third-party boards are rejected. Writes to{' '}
                  <code className="text-xs bg-gray-100 text-gray-800 px-1 rounded border border-gray-200">
                    job-post-urls
                  </code>{' '}
                  and{' '}
                  <code className="text-xs bg-gray-100 text-gray-800 px-1 rounded border border-gray-200">
                    filtered-jobs
                  </code>
                  . Same pipeline as Recommended jobs Step 1 (&quot;Search Google for jobs&quot;). Requires Vertex /
                  GCP (service account, project id).
                </p>
                {resumes.length === 0 ? (
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    No resume on file. Upload one from{' '}
                    <button
                      type="button"
                      onClick={() => navigate('/jobs-for-you')}
                      className="underline font-medium"
                    >
                      Recommended jobs
                    </button>{' '}
                    first.
                  </p>
                ) : (
                  <>
                    {resumes.length > 1 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Resume</label>
                        <select
                          value={selectedResumeId}
                          onChange={(e) => setSelectedResumeId(e.target.value)}
                          className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                        >
                          <option value="">Most recently updated (default)</option>
                          {resumes.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.sourceFilename || r.id}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={geminiGrounding}
                        onChange={(e) => setGeminiGrounding(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-sm text-gray-700">
                        Google Search grounding
                      </span>
                      <span className="text-xs text-gray-400">(better results, doesn&apos;t scale)</span>
                    </label>
                    <button
                      type="button"
                      onClick={handleGeminiFetch}
                      disabled={geminiLoading}
                      className="px-5 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {geminiLoading ? 'Calling Gemini…' : 'Fetch Google Careers jobs'}
                    </button>
                    {geminiResult && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm text-emerald-900">
                        <p className="font-medium">{geminiResult.message}</p>
                        <p className="text-xs mt-1">Resume id: {geminiResult.resumeId}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <h3 className="text-lg font-medium text-red-800 mb-2">Error</h3>
              <p className="text-red-600">{error}</p>
            </div>
          )}

          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-sm text-gray-600 hover:text-gray-900 underline"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobSearchDashboard;
